import ProductAddToCart from '@common/components/ProductAddToCart';
import Share from '@common/components/Share';
import VerticalGallery from '@common/components/VerticalGallery';
import Product from '@common/models/Product';
import StoreInfo from '@common/models/StoreInfo';
import Variant from '@common/models/Variant';
import { addProductToCart } from '@common/store/cart/slice';
import { subtractProductFromCart } from '@common/store/cart/slice';
import { addToast } from '@common/store/toasts/slice';
import { formatTimestamp2 } from '@common/utils/date';
import Button from '@lib/Button';
import Editor from '@lib/Editor';
import React, { useState } from 'react';

interface EventProps {
  product: Product;
  addProductToCart: typeof addProductToCart;
  cartProductQuantity: number;
  addToast: typeof addToast;
  storeType: string;
  subtractProductFromCart: typeof subtractProductFromCart;
  isPaymentsDisabled: boolean;
  isStoreInfoFetching: boolean;
  wishListFeature: boolean;
  storeInfo: StoreInfo;
}

const Event: React.FC<EventProps> = ({
  product,
  addProductToCart,
  subtractProductFromCart,
  cartProductQuantity,
  addToast,
  storeType,
  isPaymentsDisabled,
  isStoreInfoFetching,
  wishListFeature,
  storeInfo,
}) => {
  const [readMore, setReadMore] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState<Product | Variant>(product);
  const finalProduct = selectedVariant ?? product;
  const [eventStartDate, eventStartTime] = formatTimestamp2(finalProduct.event?.startDate);
  const [eventEndDate, eventEndTime] = formatTimestamp2(finalProduct.event?.endDate);
  return (
    <div className="container mx-auto my-24 p-16">
      <div className="bg-white rouded shadow-productDetailCard p-16">
        <VerticalGallery images={finalProduct.images.length ? finalProduct.images : product.images} />
        <div className="flex flex-wrap mt-24">
          <div className="w-full md:w-2/3 md:pr-16">
            <div className="font-semibold text-20 mb-8 break-words">{finalProduct.title}</div>
            <div className="text-14 mb-16">{finalProduct.categories?.map((category) => category.name).join(', ')}</div>
            <div className="flex flex-wrap">
              <div className="w-full md:w-1/3 mb-16 md:mb-0">
                <div className="text-12 text-event-label">START TIME</div>
                <div className="text-16 font-bold break-words">
                  {eventStartDate} {eventStartTime}
                </div>
              </div>
              <div className="w-full md:w-1/3 mb-16 md:mb-0">
                <div className="text-12 text-event-label">END TIME</div>
                <div className="text-16 font-bold break-words">
                  {eventEndDate} {eventEndTime}
                </div>
              </div>
              <div className="w-full md:w-1/3">
                <div className="text-12 text-event-label">VENUE</div>
                <div className="text-16 font-bold break-words">{finalProduct.event?.venue}</div>
              </div>
            </div>
            <hr className="my-24 border-divider" />
            <div>
              <Editor
                isDefaultFont
                name="eventShortDescription"
                value={finalProduct.shortDescription || product.shortDescription}
                readonly={true}
              />
            </div>
            {!readMore ? (
              <Button className="text-13 text-theme mt-9 focus:outline-none" onClick={() => setReadMore(true)}>
                Read More
              </Button>
            ) : (
              <Editor
                isDefaultFont
                name="eventLongDescription"
                value={finalProduct.description || product.description}
                readonly={true}
              />
            )}
          </div>
          <div className="w-full mt-32 md:mt-0 md:w-1/3 md:pl-16">
            <Share className="mb-26" product={product} />
            <ProductAddToCart
              taxIncludedInventory={storeInfo?.storeInfo?.tax_included_inventory}
              product={product}
              setSelectedVariant={setSelectedVariant}
              addToast={addToast}
              cartProductQuantity={cartProductQuantity}
              addProductToCart={addProductToCart}
              subtractProductFromCart={subtractProductFromCart}
              storeType={storeType}
              isPaymentsDisabled={isPaymentsDisabled}
              isStoreInfoFetching={isStoreInfoFetching}
              wishListFeature={wishListFeature}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Event;
